import { waitTimer } from '@/helpers/timer'
import { resetCacheByListItemType } from '@/helpers/cache'
import { backend } from '@/services/backend'

import { DocSortOption, DocsView } from './doc'
import { ListDescriptionView } from './docs.list.description'
import {
  ApiResultDocs,
  DocGroupItem,
  DocListItem,
  DocType,
  EpisodeListItem,
  ListItem,
} from './interfaces'

export class PodcastEpisodesView extends DocsView {
  // When set to true, the episode card will display the podcast title
  // as a subtitle, otherwise it will display the episode blurb.
  private _showPodcastAsSubtitle: boolean

  constructor(
    docs: DocListItem[],
    showPodcastAsSubtitle: boolean = false,
    sortOption: DocSortOption = DocSortOption.first_published_at,
  ) {
    super(docs, [], undefined)
    this._showCtaBlock = false
    this._showPodcastAsSubtitle = showPodcastAsSubtitle
    this._sort = sortOption
  }

  static async createView(
    response: ApiResultDocs,
    showPodcastAsSubtitle: boolean = false,
    defaultSort: DocSortOption = DocSortOption.first_published_at,
  ): Promise<PodcastEpisodesView> {
    const view = new PodcastEpisodesView(
      response.data,
      showPodcastAsSubtitle,
      defaultSort,
    )
    await view.init()
    return view
  }

  static async createSearchView(
    searchTerm: string = '',
  ): Promise<PodcastEpisodesView> {
    const episodes = await DocsView.searchDocsLimited(
      'podcast_episode',
      searchTerm,
    )

    const view = new PodcastEpisodesView(episodes)
    await view.init()
    return view
  }

  public docTypeName(): DocType {
    return 'podcast_episode'
  }

  public startLinkParams(episode: EpisodeListItem): Record<string, unknown> {
    let routeName = 'podcast.preview'
    if (this.continueReading) {
      routeName = episode.position_chapter ? 'podcast.page' : 'podcast.preview'
    }
    return {
      name: routeName,
      params: {
        url_slug: episode.url_slug,
      },
    }
  }

  public showPodcastAsSubtitle(): boolean {
    return this._showPodcastAsSubtitle
  }
}

/**
 * Description view for the podcast list page.
 */
export class ListDescriptionViewPodcast extends ListDescriptionView {
  public podcast: DocGroupItem
  public originalUrl: string | undefined
  author: string | undefined

  private _followView: PodcastFollowView

  constructor(docGroup: ListItem, docsNumber: string) {
    super(docGroup, docsNumber)
    this.podcast = docGroup as DocGroupItem
    this.originalUrl = this.podcast.original_url
    this.author = this.podcast.author

    this._followView = new PodcastFollowView(
      this.podcast.is_followed,
      this.podcast.id,
    )
  }

  get followView(): PodcastFollowView {
    return this._followView
  }

  get isFollowed(): boolean {
    return this._followView.isFollowed
  }
}

export class PodcastFollowView {
  private _podcastId: string
  private _hintTimeout: ReturnType<typeof setTimeout> = setTimeout(() => {})
  public isFollowed: boolean
  public isLoading = false
  public label: 'Follow' | 'Following' | 'Unfollow'
  public hint: string | null = null

  constructor(isFollowed: boolean, podcastId: string) {
    this.isFollowed = isFollowed
    this._podcastId = podcastId
    this.label = this.isFollowed ? 'Following' : 'Follow'
  }

  /**
   * Follows / Unfollows the podcast, and update the button label accordingly.
   */
  async toggleFollow(): Promise<void> {
    if (this.isLoading) {
      return
    }

    this.isLoading = true

    const [result] = await Promise.all([
      this._sendToggleFollowRequest(),
      waitTimer(500),
    ])

    this.isFollowed = result.is_followed

    // Reset cache to force fresh data loading.
    // Otherwise, we mark the podcast as followed on the podcast page, for example,
    // then open the home page and the podcast is not marked as followed (additional
    // page refresh is needed to see the followed mark).
    await resetCacheByListItemType('podcast')

    this.isLoading = false
    this.updateLabel()

    if (this.isFollowed) {
      this.showHint()
    }
  }

  showHint(): void {
    clearTimeout(this._hintTimeout)

    this.hint =
      "You're following this podcast, check the latest episodes on the podcasts page for updates."

    this._hintTimeout = setTimeout(() => {
      this.hint = null
    }, 5000)
  }

  hideHint(): void {
    clearTimeout(this._hintTimeout)
    this.hint = null
  }

  /**
   * Updates the button label.
   *
   * If the podcast is not followed, the label is "Follow".
   * If the podcast is followed, the label is "Following".
   * If the podcast is followed and the user hovers over the button,
   * the label is "Unfollow".
   *
   * @param event - The mouse event that triggered the function.
   */
  updateLabel(event?: MouseEvent): void {
    if (this.isLoading) {
      return
    }

    if (event && this.isFollowed) {
      this.label = event.type === 'mouseenter' ? 'Unfollow' : 'Following'
      return
    }

    this.label = this.isFollowed ? 'Following' : 'Follow'
  }

  /**
   * Sends the backend request to follow / unfollow the podcast.
   *
   * If the podcast is followed, send a request to unfollow.
   * If the podcast is not followed, send a request to unfollow.
   *
   * @returns an object with the updated podcast data.
   */
  private async _sendToggleFollowRequest(): Promise<DocGroupItem> {
    return this.isFollowed
      ? await backend.unfollow(this._podcastId)
      : await backend.follow(this._podcastId)
  }
}
